import { useMemo } from 'react';
import { PortalDsBadge } from 'src/components/portal-design-system';
import { DataTable, type DataTableColumn } from 'src/components/shared';
import type { PortalDsBadgeVariant } from 'src/components/portal-design-system';
import {
  formatStatusLabel,
  resolvePortalUserUsage,
} from '../utils/portal-users.mappers';
import type { AdminSettableSubscriptionTier, PortalUser } from '../types/users.types';
import { QuotaOverrideEditor } from './quota-override-editor';
import { SubscriptionTierEditor } from './subscription-tier-editor';
import { UsageBreakdownCell } from './usage-breakdown-cell';
import { UserActionsMenu } from './user-actions-menu';

const STATUS_VARIANTS: Record<PortalUser['status'], PortalDsBadgeVariant> = {
  active: 'green',
  blocked: 'red',
};

interface UsersTableProps {
  users: PortalUser[];
  totalCount: number;
  page: number;
  pageSize: number;
  globalCreativeQuota?: number;
  onPageChange: (page: number) => void;
  onBlock: (userId: string) => void;
  onUnblock: (userId: string) => void;
  onSaveQuotaOverride: (userId: string, value: number | null) => Promise<{ ok: boolean; message?: string }>;
  onSaveSubscriptionTier: (
    workspaceId: string,
    tier: AdminSettableSubscriptionTier
  ) => Promise<{ ok: boolean; message?: string }>;
  actionsDisabled?: boolean;
}

export function UsersTable({
  users,
  totalCount,
  page,
  pageSize,
  globalCreativeQuota,
  onPageChange,
  onBlock,
  onUnblock,
  onSaveQuotaOverride,
  onSaveSubscriptionTier,
  actionsDisabled = false,
}: UsersTableProps) {
  const columns = useMemo<DataTableColumn<PortalUser>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      accessor: (user) => user.name,
      render: (user) => (
        <span className="font-medium text-[#0D0F1A]">
          {user.name}
        </span>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      accessor: (user) => user.email,
      render: (user) => (
        <span className="text-[#5E6278]">
          {user.email}
        </span>
      ),
    },
    {
      id: 'subscription',
      header: 'Subscription',
      accessor: (user) => user.subscription,
      render: (user) => (
        <SubscriptionTierEditor
          workspaceId={user.workspace_id}
          tier={user.subscription}
          disabled={actionsDisabled}
          onSave={onSaveSubscriptionTier}
        />
      ),
    },
    {
      id: 'usage',
      header: 'Usage (30d)',
      accessor: (user) => user.monthly_used,
      render: (user) => (
        <UsageBreakdownCell usage={resolvePortalUserUsage(user)} />
      ),
    },
    {
      id: 'creativeQuota',
      header: 'Creative quota',
      accessor: (user) => user.creative_quota_override ?? globalCreativeQuota ?? user.monthly_limit,
      render: (user) => (
        <QuotaOverrideEditor
          value={user.creative_quota_override}
          globalDefault={globalCreativeQuota ?? user.monthly_limit}
          disabled={actionsDisabled}
          onSave={(value) => onSaveQuotaOverride(user.id, value)}
        />
      ),
    },
    {
      id: 'pendingCampaigns',
      header: 'Pending campaigns',
      accessor: (user) => user.pending_campaigns,
      align: 'right',
      render: (user) => (
        user.pending_campaigns === 0
          ? (
            <span className="text-[#9CA3AF]">
              —
            </span>
          )
          : (
            <span className="font-mono text-[13px]">
              {user.pending_campaigns}
            </span>
          )
      ),
    },
    // created_at is available from the API but omitted here to keep the table width manageable.
    {
      id: 'status',
      header: 'Status',
      accessor: (user) => user.status,
      render: (user) => (
        <PortalDsBadge
          variant={STATUS_VARIANTS[user.status]}
          pill
        >
          {formatStatusLabel(user.status)}
        </PortalDsBadge>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: () => '',
      render: (user) => (
        <UserActionsMenu
          user={user}
          disabled={actionsDisabled}
          onBlock={() => onBlock(user.id)}
          onUnblock={() => onUnblock(user.id)}
        />
      ),
    },
  ], [actionsDisabled, globalCreativeQuota, onBlock, onSaveQuotaOverride, onSaveSubscriptionTier, onUnblock]);

  return (
    <DataTable
      rows={users}
      columns={columns}
      pageSize={pageSize}
      getRowKey={(user) => user.id}
      emptyMessage="No users match your search or filters. Try adjusting the criteria above."
      serverPagination={{
        page,
        pageSize,
        totalCount,
        onPageChange,
      }}
    />
  );
}
